import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download, FilePlus2, Save, Trash2 } from "lucide-react";
import {
  ReceiptType,
  ReportGroupBy,
  ReportPeriod,
  ReportResult,
  ReportRow,
  reports,
  ReportSpec,
  ReportTotal,
  SavedReport,
} from "../api/client";
import MoneyTotals from "../components/MoneyTotals";

const GROUP_LABEL: Record<ReportGroupBy, string> = {
  counterparty: "Vendor / client",
  card: "Card",
  type: "Type",
  month: "Month",
  quarter: "Quarter",
  year: "Year",
};

const PERIOD_LABEL: Record<ReportPeriod, string> = {
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  last_quarter: "Last quarter",
  this_year: "This year",
  last_year: "Last year",
  last_12_months: "Last 12 months",
  all: "All time",
  custom: "Custom dates",
};

const TYPES: { value: ReceiptType; label: string }[] = [
  { value: "receipt", label: "Receipts" },
  { value: "bill", label: "Bills" },
  { value: "income", label: "Income" },
];

const TYPE_LABEL: Record<string, string> = { receipt: "Receipt", bill: "Bill", income: "Income" };

const BLANK: ReportSpec = {
  group_by: "counterparty",
  types: ["receipt", "bill"],
  period: "this_year",
  date_from: null,
  date_to: null,
  status: null,
};

/** Ready-made starting points; saving one makes it the user's own. */
const TEMPLATES: { name: string; spec: ReportSpec }[] = [
  { name: "Spending by vendor", spec: BLANK },
  { name: "Income by client", spec: { ...BLANK, types: ["income"] } },
  { name: "Monthly spending", spec: { ...BLANK, group_by: "month", period: "last_12_months" } },
  { name: "Spending by card", spec: { ...BLANK, group_by: "card" } },
  { name: "Tax paid by quarter", spec: { ...BLANK, group_by: "quarter" } },
];

function rowLabel(row: ReportRow, groupBy: ReportGroupBy): string {
  if (row.key) return groupBy === "type" ? TYPE_LABEL[row.key] ?? row.key : row.key;
  return groupBy === "card" ? "No card" : groupBy === "counterparty" ? "(no name)" : "—";
}

const taxOf = (totals: ReportTotal[]) =>
  totals.filter((t) => t.tax_cents).map((t) => ({ currency: t.currency, total_cents: t.tax_cents }));

function csv(result: ReportResult, title: string): string {
  // One line per group and currency: amounts in different currencies are never added.
  const cell = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
  const lines = [[GROUP_LABEL[result.group_by], "Documents", "Currency", "Total", "Tax"].map(cell).join(",")];
  const add = (label: string, count: number, totals: ReportTotal[]) => {
    for (const t of totals.length ? totals : [{ currency: "", total_cents: 0, tax_cents: 0 }])
      lines.push([label, count, t.currency ?? "", (t.total_cents / 100).toFixed(2), (t.tax_cents / 100).toFixed(2)]
        .map(cell).join(","));
  };
  result.rows.forEach((r) => add(rowLabel(r, result.group_by), r.count, r.totals));
  add(`Total (${title})`, result.count, result.totals);
  return lines.join("\n");
}

const sameSpec = (a: ReportSpec, b: ReportSpec) => JSON.stringify(a) === JSON.stringify(b);

export default function Reports() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [saved, setSaved] = useState<SavedReport[]>([]);
  const [spec, setSpec] = useState<ReportSpec>(BLANK);
  const [name, setName] = useState(TEMPLATES[0].name);
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const openId = params.get("report");
  const open = saved.find((r) => r.id === openId) ?? null;
  const dirty = open ? !sameSpec(open.spec, spec) || open.name !== name : false;

  useEffect(() => {
    reports.list().then(setSaved).catch((e) => setError(e instanceof Error ? e.message : "Could not load reports"));
  }, []);

  // Open a saved report from the URL once the list is in.
  useEffect(() => {
    if (open) {
      setSpec(open.spec);
      setName(open.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open?.id]);

  // Re-run whenever the spec changes (custom dates wait until both ends make sense).
  useEffect(() => {
    if (spec.types.length === 0) return;
    if (spec.period === "custom" && (!spec.date_from || !spec.date_to)) return;
    if (spec.period === "custom" && spec.date_from! > spec.date_to!) {
      setError("The start date is after the end date.");
      return;
    }
    let stale = false;
    setLoading(true);
    setError(null);
    reports
      .run(spec)
      .then((r) => !stale && setResult(r))
      .catch((e) => !stale && setError(e instanceof Error ? e.message : "Could not run the report"))
      .finally(() => !stale && setLoading(false));
    return () => {
      stale = true;
    };
  }, [spec]);

  function update(changes: Partial<ReportSpec>) {
    setNotice(null);
    setSpec((s) => ({ ...s, ...changes }));
  }

  function toggleType(t: ReceiptType) {
    const types = spec.types.includes(t) ? spec.types.filter((x) => x !== t) : [...spec.types, t];
    update({ types: TYPES.map((x) => x.value).filter((x) => types.includes(x)) });
  }

  function openReport(id: string | null) {
    const next = new URLSearchParams(params);
    if (id) next.set("report", id);
    else next.delete("report");
    setParams(next, { replace: true });
    setNotice(null);
  }

  function startFrom(template: { name: string; spec: ReportSpec }) {
    openReport(null);
    setSpec(template.spec);
    setName(template.name);
  }

  async function act(fn: () => Promise<string>) {
    setError(null);
    try {
      setNotice(await fn());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    }
  }

  const save = () =>
    act(async () => {
      if (open) {
        const r = await reports.update(open.id, { name: name.trim() || open.name, spec });
        setName(r.name);
        setSaved((list) => list.map((x) => (x.id === r.id ? r : x)).sort((a, b) => a.name.localeCompare(b.name)));
        return `Saved “${r.name}”.`;
      }
      const r = await reports.save(name.trim() || "Untitled report", spec);
      setSaved((list) => [...list, r].sort((a, b) => a.name.localeCompare(b.name)));
      openReport(r.id);
      return `Saved “${r.name}”. It's in your reports on the left.`;
    });

  const saveAsNew = () =>
    act(async () => {
      const r = await reports.save(`${name.trim() || "Untitled report"} (copy)`, spec);
      setSaved((list) => [...list, r].sort((a, b) => a.name.localeCompare(b.name)));
      setName(r.name);
      openReport(r.id);
      return `Saved a copy as “${r.name}”.`;
    });

  const remove = () =>
    act(async () => {
      if (!open || !window.confirm(`Delete the report “${open.name}”?`)) return "";
      await reports.remove(open.id);
      setSaved((list) => list.filter((x) => x.id !== open.id));
      openReport(null);
      return `Deleted “${open.name}”.`;
    });

  function exportCsv() {
    if (!result) return;
    const blob = new Blob([csv(result, name)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(name || "report").replace(/[^\w -]+/g, "").trim() || "report"}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // Vendors/clients and cards open their own pages.
  function rowLink(row: ReportRow): string | null {
    if (!row.key) return null;
    if (spec.group_by === "counterparty") {
      const base = spec.types.length === 1 && spec.types[0] === "income" ? "/app/clients" : "/app/vendors";
      return `${base}/name:${encodeURIComponent(row.key)}`;
    }
    if (spec.group_by === "card") return `/app/cards/${row.key.slice(-4)}`;
    return null;
  }

  const range = useMemo(() => {
    if (!result) return "";
    if (!result.date_from && !result.date_to) return "All time";
    return `${result.date_from ?? "…"} to ${result.date_to ?? "today"}`;
  }, [result]);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Reports</h1>
        <p className="page-sub">
          Group your ledger any way you need. Totals are kept per currency, never added across currencies.
        </p>
      </div>

      {error && <div className="page-error">{error}</div>}
      {notice && <div className="page-toast">{notice}</div>}

      <div className="reports-layout">
        <aside className="reports-side">
          <div className="reports-side-head">Your reports</div>
          {saved.length === 0 ? (
            <p className="muted reports-side-empty">Nothing saved yet. Build a report and save it.</p>
          ) : (
            saved.map((r) => (
              <button key={r.id} className={"reports-pick" + (r.id === openId ? " active" : "")}
                onClick={() => openReport(r.id)}>
                {r.name}
              </button>
            ))
          )}
          <div className="reports-side-head">Start from</div>
          {TEMPLATES.map((t) => (
            <button key={t.name} className="reports-pick" onClick={() => startFrom(t)}>
              <FilePlus2 size={14} /> {t.name}
            </button>
          ))}
        </aside>

        <section className="reports-main">
          <div className="detail-card reports-builder">
            <div className="reports-name">
              <input aria-label="Report name" value={name} onChange={(e) => setName(e.target.value)} />
              <div className="reports-actions">
                <button className="btn btn-primary btn-sm" onClick={save} disabled={open ? !dirty : false}>
                  <Save size={14} /> {open ? "Save changes" : "Save report"}
                </button>
                {open && (
                  <>
                    <button className="btn btn-ghost btn-sm" onClick={saveAsNew}>Save as new</button>
                    <button className="btn btn-ghost btn-sm" onClick={remove} aria-label="Delete report">
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="filter-bar reports-fields">
              <label>
                Group by
                <select value={spec.group_by} onChange={(e) => update({ group_by: e.target.value as ReportGroupBy })}>
                  {(Object.keys(GROUP_LABEL) as ReportGroupBy[]).map((g) => (
                    <option key={g} value={g}>{GROUP_LABEL[g]}</option>
                  ))}
                </select>
              </label>
              <label>
                Period
                <select value={spec.period} onChange={(e) => update({ period: e.target.value as ReportPeriod })}>
                  {(Object.keys(PERIOD_LABEL) as ReportPeriod[]).map((p) => (
                    <option key={p} value={p}>{PERIOD_LABEL[p]}</option>
                  ))}
                </select>
              </label>
              {spec.period === "custom" && (
                <>
                  <label>
                    From
                    <input type="date" value={spec.date_from ?? ""} onChange={(e) => update({ date_from: e.target.value || null })} />
                  </label>
                  <label>
                    To
                    <input type="date" value={spec.date_to ?? ""} onChange={(e) => update({ date_to: e.target.value || null })} />
                  </label>
                </>
              )}
              <label>
                Status
                <select value={spec.status ?? ""}
                  onChange={(e) => update({ status: (e.target.value || null) as ReportSpec["status"] })}>
                  <option value="">Any</option>
                  <option value="pending">Needs review</option>
                  <option value="reviewed">Reviewed</option>
                </select>
              </label>
              <fieldset className="reports-types">
                <legend>Include</legend>
                {TYPES.map((t) => (
                  <label key={t.value} className="reports-check">
                    <input type="checkbox" checked={spec.types.includes(t.value)} onChange={() => toggleType(t.value)} />
                    {t.label}
                  </label>
                ))}
              </fieldset>
            </div>
          </div>

          {spec.types.length === 0 ? (
            <div className="page-empty">Pick at least one of receipts, bills or income.</div>
          ) : !result ? (
            <div className="page-empty">{loading ? "Running…" : "No results."}</div>
          ) : (
            <>
              <div className="reports-result-head">
                <span className="muted">
                  {range} · {result.count} document{result.count === 1 ? "" : "s"}
                  {loading && " · updating…"}
                </span>
                <button className="btn btn-ghost btn-sm" onClick={exportCsv} disabled={result.rows.length === 0}>
                  <Download size={14} /> Export CSV
                </button>
              </div>
              {result.rows.length === 0 ? (
                <div className="page-empty">Nothing in this period.</div>
              ) : (
                <table className="data-table reports-table">
                  <thead>
                    <tr>
                      <th>{GROUP_LABEL[result.group_by]}</th>
                      <th style={{ textAlign: "right" }}>Documents</th>
                      <th style={{ textAlign: "right" }}>Total</th>
                      <th style={{ textAlign: "right" }}>Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row) => {
                      const link = rowLink(row);
                      return (
                        <tr key={row.key} className={link ? "clickable" : ""} onClick={() => link && navigate(link)}>
                          <td>{rowLabel(row, result.group_by)}</td>
                          <td className="num">{row.count}</td>
                          <td className="num"><MoneyTotals totals={row.totals} /></td>
                          <td className="num"><MoneyTotals totals={taxOf(row.totals)} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td className="num">{result.count}</td>
                      <td className="num"><MoneyTotals totals={result.totals} /></td>
                      <td className="num"><MoneyTotals totals={taxOf(result.totals)} /></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
